/* Design tokens for the PUBLIC MARKETING pages (/, /hebe, /hebe/rooms/*, /insights, legal).
 *
 * Single source of truth. Before this file the palette was ~40 inline hex literals spread across
 * every component, with two golds (#c9a86c and #c9a961), two inks (#1a1a1a and #141414) and seven
 * breakpoints (768/480/1024/968/640/900/1200) expressing three distinct intents.
 *
 * Deliberately a plain object, imported directly — NOT a styled-components ThemeProvider. These
 * pages are server-rendered and hydrated (entrypoints/hebe.jsx + ssr.jsx); a plain import resolves
 * identically on both sides and cannot drift.
 *
 * Scope: marketing only. The Front Office kit (pages/frontdesk_v2/_kit/theme.js) and the guest
 * portal still run the old gold-led palette. That divergence is deliberate and temporary — staff
 * tooling and print collateral follow later.
 */

/* Gold is now a HIGHLIGHT, not the lead: hairlines, eyebrows, active states, focus rings. It never
 * carries body text on a light ground — #C9A86C on the bone canvas is ~2.05:1, far below the 4.5:1
 * WCAG AA needs. Use `goldText` on the rare occasion gold has to be read.
 */
export const color = {
  primary: "#1F3D2B", // deep forest — the lead accent
  primaryHover: "#2A5139",
  primarySoft: "#E8EDE7", // tinted ground for soft panels
  primaryText: "#16452C", // 9.9:1 on canvas — safe for text

  gold: "#C9A86C", // highlight ONLY: rules, eyebrows, active states
  goldText: "#7F6236", // 5.2:1 on canvas — the readable gold
  goldSoft: "#F5EEE0",

  ink: "#14150F", // headings, primary text
  ink700: "#3D3F36", // secondary text
  ink500: "#6B6D62", // muted text — 4.7:1 on canvas, still AA

  canvas: "#F6F4EE", // bone — the default page ground
  canvasAlt: "#EFEBE1", // banded sections
  surface: "#FFFFFF", // cards, panels
  border: "#E2DED2",
  borderStrong: "#CFC9B9",

  onDark: "#F6F4EE", // text on ink/photography
  onDarkMuted: "rgba(246, 244, 238, 0.72)",

  // Scrims for text over photography. The hero needs the strong one — the old page ran a 0.4/0.5/0.7
  // gradient and the lede was still barely legible over high-detail interior shots.
  //
  // Two layers, because the hero copy is anchored bottom-LEFT while the real footage is bright and
  // high-contrast: the vertical pass alone left the lede sitting on pale signage. The horizontal
  // pass darkens the text column specifically and fades out well before the right edge, so the
  // image is still the image.
  scrim:
    "linear-gradient(100deg, rgba(20,21,15,0.82) 0%, rgba(20,21,15,0.55) 38%, rgba(20,21,15,0.10) 70%, rgba(20,21,15,0) 100%), " +
    "linear-gradient(180deg, rgba(20,21,15,0.30) 0%, rgba(20,21,15,0.42) 50%, rgba(20,21,15,0.72) 100%)",
  scrimSoft:
    "linear-gradient(180deg, rgba(20,21,15,0.15) 0%, rgba(20,21,15,0.45) 100%)",

  focus: "#C9A86C",
};

/* Fraunces for display, Inter for body — both OFL, self-hosted as variable woff2 from public/fonts
 * (see public/fonts/README.md). Self-hosted rather than linked so the pages make no third-party
 * request and nothing blocks first paint but our own origin.
 */
export const font = {
  display: `"Fraunces", ui-serif, Georgia, Cambria, "Times New Roman", serif`,
  body: `"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif`,
};

/* Fluid where it earns it (headings), fixed where predictability matters (body).
 * Ratio is a 1.25 major third at the small end.
 */
export const type = {
  display: "clamp(2.75rem, 1.6rem + 4.2vw, 4.5rem)", // 44 -> 72
  h1: "clamp(2.25rem, 1.5rem + 3vw, 3.5rem)", // 36 -> 56
  h2: "clamp(1.875rem, 1.4rem + 2vw, 2.75rem)", // 30 -> 44
  h3: "clamp(1.375rem, 1.15rem + 1vw, 1.75rem)", // 22 -> 28
  h4: "1.25rem",
  lg: "1.125rem",
  base: "1rem",
  sm: "0.875rem",
  xs: "0.75rem",
  eyebrow: "0.75rem",
};

export const leading = {
  tight: 1.1,
  snug: 1.25,
  normal: 1.55,
  relaxed: 1.75,
};

export const tracking = {
  tight: "-0.02em",
  normal: "0",
  wide: "0.08em",
  eyebrow: "0.18em",
};

export const weight = {
  regular: 400,
  medium: 500,
  semibold: 600,
};

/* 4px base. Named by step so a component never types a magic number. */
export const space = {
  0: "0",
  1: "4px",
  2: "8px",
  3: "12px",
  4: "16px",
  5: "24px",
  6: "32px",
  7: "48px",
  8: "64px",
  9: "96px",
  10: "128px",
};

/* Vertical rhythm for a page section. The old page varied section padding ad hoc, which is most of
 * why it read as ragged.
 */
export const sectionPad = {
  y: "clamp(64px, 8vw, 128px)",
  x: "clamp(20px, 5vw, 40px)",
};

export const radius = {
  none: "0",
  sm: "4px",
  md: "8px",
  lg: "16px",
  pill: "999px",
};

export const shadow = {
  sm: "0 1px 2px rgba(20, 21, 15, 0.06)",
  md: "0 4px 16px rgba(20, 21, 15, 0.08)",
  lg: "0 16px 48px rgba(20, 21, 15, 0.12)",
};

export const container = {
  wide: "1280px",
  default: "1140px",
  narrow: "760px", // long-form prose — legal pages, article bodies
};

/* Three breakpoints, replacing seven. `md` is the one real layout break. */
export const bp = {
  sm: 480,
  md: 768,
  lg: 1024,
  xl: 1280,
};

/* Usage: ${media.md`grid-template-columns: 1fr;`} — max-width, matching the existing
 * desktop-first components so nothing has to be inverted during the migration.
 */
export const media = Object.fromEntries(
  Object.entries(bp).map(([key, px]) => [
    key,
    (strings, ...values) =>
      `@media (max-width: ${px}px) { ${String.raw({ raw: strings }, ...values)} }`,
  ]),
);

export const motion = {
  fast: "160ms cubic-bezier(0.4, 0, 0.2, 1)",
  base: "260ms cubic-bezier(0.4, 0, 0.2, 1)",
  slow: "420ms cubic-bezier(0.4, 0, 0.2, 1)",
};

export const z = {
  base: 1,
  sticky: 100,
  header: 200,
  widget: 300,
  overlay: 400,
  modal: 500,
};

export const tokens = {
  color,
  font,
  type,
  leading,
  tracking,
  weight,
  space,
  sectionPad,
  radius,
  shadow,
  container,
  bp,
  media,
  motion,
  z,
};

export default tokens;
